import React, {useState} from "react"
import {Alert, Button, Card, Checkbox, Col, Flex, Input, List, Row, Typography} from "antd";
import dayjs from "dayjs";

export const SurveySend = ({respondents, setSurveyRespondents, surveyId}) => {
    const [filterValue, setFilterValue] = useState(null);
    const [unmatchedIds, setUnmatchedIds] = useState([]);

    const removeFromList = (email: any, checked: any) => {
        setSurveyRespondents(respondents.map((resp: any) => (resp.email === email ? {
            ...resp,
            disabled: !checked
        } : resp)))
    }

    const checkAllHandler = () => {
        const status = checkStatus();

        setSurveyRespondents(respondents.map((resp: any) => ({
            ...resp,
            disabled: status
        })))
    }

    const checkStatus = () => {
        return respondents.filter((a: any) => a.disabled).length === 0
    }

    const filterMatch = (item: any) => {
        let filterValueUpdated = filterValue ? filterValue.toLowerCase() : null;
        return filterValueUpdated ? (
            (
                item.name?.toLowerCase().match(filterValueUpdated)
                || item.firstName?.toLowerCase().match(filterValueUpdated)
                || item.lastName?.toLowerCase().match(filterValueUpdated) ||
                item.email?.toLowerCase().match(filterValueUpdated)
            ) ? item : null) : item
    }

    const parseIdsFromText = (text: string): string[] => {
        if (!text) return [];
        const lines = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
        if (!lines.length) return [];

        const delimiter = lines[0].includes('\t') ? '\t' : ',';
        const headerCells = lines[0].split(new RegExp(`${delimiter}(?=(?:[^\"]*\"[^\"]*\")*[^\"]*$)`)).map((h: any) => h.replaceAll('"', '').trim().toLowerCase());
        let ids: string[] = [];

        const idHeaderCandidates = ['Id [Person]', 'Id'];
        const idIndex = headerCells.findIndex(h => idHeaderCandidates.includes(h));

        if (lines[0].includes(delimiter) && idIndex > -1) {
            for (let i = 1; i < lines.length; i++) {
                const cells = lines[i].split(new RegExp(`${delimiter}(?=(?:[^\"]*\"[^\"]*\")*[^\"]*$)`)).map((c: any) => c.replaceAll('"', '').trim());
                const val = cells[idIndex];
                if (val !== undefined && val !== '') ids.push(val);
            }
        } else if (lines[0].includes(delimiter)) {
            for (let i = 0; i < lines.length; i++) {
                const cells = lines[i].split(new RegExp(`${delimiter}(?=(?:[^\"]*\"[^\"]*\")*[^\"]*$)`)).map((c: any) => c.replaceAll('"', '').trim());
                const val = cells[0];
                if (val !== undefined && val !== '') ids.push(val);
            }
        } else {
            ids = lines;
        }

        return Array.from(new Set(ids.map(x => x.trim()).filter(x => x.length > 0)));
    }

    const applyIdSelection = (ids: string[]) => {
        const idSet = new Set(ids.map(x => x.toString()));
        const allIdsInSystem = new Set((respondents || []).map((r: any) => (r.id ?? '').toString()));
        const unmatched = ids.filter(x => !allIdsInSystem.has(x.toString()));
        setUnmatchedIds(unmatched);

        setSurveyRespondents((respondents || []).map((resp: any) => ({
                ...resp,
                disabled: !idSet.has((resp.id ?? '').toString())
            }))
        );
    }

    const handleFileUpload = (e: any) => {
        const file = e?.target?.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => {
            try {
                const text = reader.result as string;
                const ids = parseIdsFromText(text);
                applyIdSelection(ids);
            } catch (err) {
                console.error('Failed to parse IDs file', err);
            }
        };
        reader.readAsText(file);
        e.target.value = '';
    }

    return (
        <Col xs={24}>
            <Row gutter={8}>
                <Col xs={12}>
                    <Button type={'primary'} ghost onClick={checkAllHandler}
                            className={'mb-2 text-right'}>{checkStatus() ? 'Un-Check' : 'Check'} All</Button>
                </Col>
                <Col xs={12}>
                    <Input placeholder={'Search for youth'} allowClear={true} value={filterValue}
                           onChange={(e) => setFilterValue(e.target.value)}/>
                </Col>
            </Row>
            <Row gutter={8} className={'mb-2'}>
                <Col xs={24}>
                    <Flex vertical gap={'small'}>
                        <Typography.Text>Upload a CSV file with user ids. We will enable only those users and list any
                            IDs that didn’t match.</Typography.Text>
                        <input type={'file'} accept={'.csv'} onChange={handleFileUpload}/>
                        {unmatchedIds.length > 0 && (
                            <Alert type={'error'} message={`Unmatched youth count: ${unmatchedIds.length}`} />
                        )}
                    </Flex>
                </Col>
            </Row>
            <List size={'small'}
                  itemLayout={'vertical'}
                  dataSource={respondents.filter((itz: any) => (filterValue ? filterMatch(itz) : itz)).filter((a: any) => a)}
                  bordered={false}
                  pagination={{size: 'small', position: 'bottom', align: 'end', total: respondents.length}}
                  renderItem={(item: any) => (
                      <List.Item>
                          <Card size={'small'}
                                title={`${item.name ? item.name : `${item.firstName} ${item.lastName}`} - ${item.email}`}
                                extra={<Checkbox onClick={(e: any) => removeFromList(item.email, e.target.checked)}
                                                 checked={!item.disabled}>Enabled</Checkbox>}>
                              {item.surveyResponses.filter((resp: any) => resp.survey_id === surveyId).filter((resp: any) => !(resp.expired)).map((response: any) =>
                                  (
                                      <Row key={response.id}>
                                          <Col xs={8}>Started: {response.started ? 'Yes' : 'No'}
                                          </Col>
                                          <Col xs={8}>
                                              Completed: {response.completed ? 'Yes' : 'No'}
                                          </Col>
                                          <Col xs={8}>
                                              Sent: {dayjs(response.created_at).format('MMMM, DD YYYY')}
                                          </Col>
                                      </Row>
                                  )
                              )}
                              {item.surveyResponses.filter((resp: any) => resp.survey_id === surveyId).length === 0 &&
                                  <Typography.Text>No data.</Typography.Text>
                              }
                          </Card>
                      </List.Item>
                  )
                  }
            />
        </Col>
    )
}